import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './ui/Modal';
import {
  PERSONAL_DESTINATION, canWriteTeamFolder, defaultTeamFolderValue, isOfferedDestination,
  parseDestination,
} from '../utils/vaultDestinations';
import './Vault.css';
import { VAULT_TYPE_META, VAULT_TYPE_ORDER, emptyValues, valuesFromItem } from './vault/vaultItemData';
import { renderVaultItemModal } from './vault/renderVaultItemModal';
import { useVaultItemForm } from './vault/useVaultItemForm';
import { useVaultKeyFile } from './vault/useVaultKeyFile';
export {
  PASSKEY_DATA_KEYS, VAULT_TYPE_META, VAULT_TYPE_ORDER, buildData, buildTeamData, generatePassword,
  omitPasskeyKeys, valuesFromItem,
} from './vault/vaultItemData';

// `initialType` (optional): preset the item type on create and skip the
// type-picker step — used by sibling extensions (e.g. FTP's "New connection"
// presets 'sftp'). Absent/null keeps the two-step behavior unchanged.
//
// TEAM VAULT PROPS, all optional and all defaulting to the pre-feature
// behaviour so a solo user's modal is unchanged:
//
//   `destinations` — from `buildDestinations()`. EMPTY means no destination
//       control is rendered at all (a user in no writable team never learns
//       this feature exists).
//   `byTeam`       — `teamVaultStore`'s raw slice, used only to derive the
//       chosen destination's folder list. Passed in rather than read from the
//       store for the same reason `folders` is: reaching into the store here
//       makes the modal untestable without standing the whole store up, which
//       is exactly what broke when it was first written that way.
//   `teamContext`  — `{ teamId, itemId, version }` when EDITING an item that
//       already lives in a team vault. Its presence routes the save to the
//       team API with optimistic concurrency; its absence keeps the personal
//       path byte-for-byte as it was.
//
// PRE-FILING PROPS (create only), from the vault sidebar's per-row "+":
//
//   `initialDestination`  — a `buildDestinations` value ('personal' or
//       'team:<id>'). IGNORED unless that destination was actually offered, so
//       a caller naming a team the user cannot write to gets the personal
//       vault rather than a form the server refuses at save.
//   `initialFolder`       — a personal folder NAME. Offered as an option even
//       when it is absent from `folders`: `folders` is derived from items, so
//       an EMPTY folder (one that exists only in the registry) is not in it,
//       and that is exactly the folder somebody is most likely to file into.
//   `initialTeamFolderId` — a team folder row id, '' for the team root.
//       Ignored unless the user may write to that specific folder.
//
// All three are IGNORED when `item` is set: an edit keeps the item where it
// already is (see vaultDestinations.js rule 3).
export default function VaultItemModal({
  isOpen,
  onClose,
  item = null,
  initialType = null,
  folders = [],
  destinations = [],
  /**
   * Opt-in: offer "don't save this at all" as a destination.
   *
   * `{ label, note, onSave }`. Picking it makes the submit call `onSave(values)`
   * instead of writing anywhere, and the modal closes. Absent by default, so
   * the Vault panel — where "a vault item you do not save" is a contradiction —
   * is untouched.
   *
   * It lives here rather than in a second form because FTP already asks this
   * exact question once, in this exact modal: "where does this go". A parallel
   * quick-connect dialog would be the same fields with a different layout and
   * its own drift (owner, 23.09.2026).
   */
  ephemeralOption = null,
  byTeam = {},
  teamContext = null,
  initialDestination = null,
  initialFolder = null,
  initialTeamFolderId = null,
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState('type');
  const [type, setType] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [saving, setSaving] = useState(false);
  const [capDraft, setCapDraft] = useState(null); // card U1: { cap, item } while the cap card asks
  const [error, setError] = useState(null);
  // SSH private key input mode: paste into the textarea, or load a file's
  // content into the same `privateKey` value (saved identically either way).
  const [keySource, setKeySource] = useState('text');
  const [keyFileName, setKeyFileName] = useState('');
  const [keyFileError, setKeyFileError] = useState(null);
  const keyFileInputRef = useRef(null);
  // Where a NEW item is saved. Create-time only: an edit stays in whatever
  // vault the item already lives in (see vaultDestinations.js rule 3).
  const [destination, setDestination] = useState(PERSONAL_DESTINATION);
  // A team item's folder is a server row id, kept OUT of `values.folder` so a
  // numeric id can never be written into the item body as a personal folder
  // name (and so switching destination cannot smuggle one across).
  const [teamFolderId, setTeamFolderId] = useState('');

  // ── pre-filing (create only) ───────────────────────────────────────────────
  //
  // Resolved out here, not inside the effect, so the effect's dependency list
  // is made of plain strings. A value rebuilt per render in the deps would
  // re-run the reset on every keystroke and wipe the form.
  //
  // Each is FAIL-CLOSED: a destination that was not offered, or a folder the
  // user may not write to, is discarded rather than pre-selected. The sidebar
  // already disables those controls; this is the guard for the caller that
  // does not.
  const seedDestination =
    !item && initialDestination && isOfferedDestination(destinations, initialDestination)
      ? initialDestination
      : PERSONAL_DESTINATION;
  const seedPersonalFolder =
    !item && parseDestination(seedDestination).kind === 'personal' && typeof initialFolder === 'string'
      ? initialFolder.trim()
      : '';
  const seedTeamId = parseDestination(seedDestination).teamId;
  let seedTeamFolderId = '';
  if (!item && seedTeamId) {
    seedTeamFolderId =
      initialTeamFolderId != null &&
      initialTeamFolderId !== '' &&
      canWriteTeamFolder(byTeam, seedTeamId, initialTeamFolderId)
        ? String(initialTeamFolderId)
        : // The root was asked for, or a folder the user cannot write to. Fall
          // back to the destination's own default — which is the root ONLY
          // when the root is writable.
          defaultTeamFolderValue(seedDestination, byTeam);
  }

  useEffect(() => {
    if (!isOpen) {
      // The component stays mounted after close (`if (!isOpen) return null`),
      // so proactively drop the decrypted secrets held in form state instead
      // of leaving them until the next open.
      setValues(emptyValues());
      setError(null);
      setSaving(false);
      setKeySource('text');
      setKeyFileName('');
      setKeyFileError(null);
      setDestination(PERSONAL_DESTINATION);
      setTeamFolderId('');
      return;
    }
    // A create starts blank, except for the folder the caller pre-filed.
    const created = () => {
      const v = emptyValues();
      if (seedPersonalFolder) v.folder = seedPersonalFolder;
      return v;
    };
    if (item) {
      setStep('form');
      setType(item.type);
      setValues(valuesFromItem(item));
    } else if (initialType && VAULT_TYPE_META[initialType]) {
      setStep('form');
      setType(initialType);
      setValues(created());
    } else {
      setStep('type');
      setType(null);
      setValues(created());
    }
    setSaving(false);
    setError(null);
    setKeySource('text');
    setKeyFileName('');
    setKeyFileError(null);
    // Was never reset, so "Create new folder…" from a previous open survived
    // into the next one — and would have swallowed a pre-filed folder into an
    // inline text box instead of showing it selected.
    setCreatingFolder(false);
    // The personal vault UNLESS this open was explicitly pre-filed elsewhere.
    // The default has to be the private one, because carrying the LAST chosen
    // team over to the next New Item is a way to file a personal credential
    // into a shared vault by accident — the failure mode is disclosure. A
    // seed is not that: it is this open, from a control the user just clicked
    // on that team, and it stays visible and changeable in the picker.
    setDestination(seedDestination);
    setTeamFolderId(
      teamContext?.folderId != null ? String(teamContext.folderId) : seedTeamFolderId
    );
  }, [isOpen, item, initialType, teamContext, seedDestination, seedPersonalFolder, seedTeamFolderId]);

  const {
    setValue, pickKeyFile, onKeyFileInputChange, pickType, NEW_FOLDER, existingFolders,
  } = useVaultKeyFile({
    t, folders, setStep, setType, setValues, setKeyFileName, setKeyFileError, keyFileInputRef,
    seedPersonalFolder,
  });
  const [creatingFolder, setCreatingFolder] = useState(false);

  const {
    changeFolder, EPHEMERAL_DESTINATION, offerEphemeral, isEphemeral, showDestination,
    isTeamDestination, teamFolderOptions, teamRootWritable, changeDestination, changeEngine,
    changeProtocol, updateCustomField, addCustomField, removeCustomField, canSave, handleSubmit,
  } = useVaultItemForm({
    values, item, type, destination, destinations, byTeam, teamContext, onClose, ephemeralOption,
    setValue, setValues, saving, setSaving, setCapDraft, setError, setDestination, teamFolderId,
    setTeamFolderId, setCreatingFolder, NEW_FOLDER,
  });

  if (!isOpen) return null;

  if (step === 'type') {
    return (
      <Modal isOpen onClose={onClose} title={t('vault.newItem')} subtitle={t('vault.chooseType')}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 10,
          }}
        >
          {VAULT_TYPE_ORDER.map((id) => {
            const { labelKey, Icon } = VAULT_TYPE_META[id];
            return (
              <button
                key={id}
                type="button"
                onClick={() => pickType(id)}
                className="ext-icon-option"
              >
                <Icon size={30} />
                <span className="text-xs font-medium text-gray-800 dark:text-zinc-200">
                  {t(labelKey)}
                </span>
              </button>
            );
          })}
        </div>
      </Modal>
    );
  }

  return renderVaultItemModal({
    t, values, item, type, destinations, destination, onClose, ephemeralOption, setValue, saving,
    capDraft, setCapDraft, error, keySource, setKeySource, keyFileName, keyFileError,
    keyFileInputRef, teamFolderId, setTeamFolderId, setCreatingFolder, pickKeyFile,
    onKeyFileInputChange, NEW_FOLDER, existingFolders, creatingFolder, changeFolder,
    EPHEMERAL_DESTINATION, offerEphemeral, isEphemeral, showDestination, isTeamDestination,
    teamFolderOptions, teamRootWritable, changeDestination, changeEngine, changeProtocol,
    updateCustomField, addCustomField, removeCustomField, canSave, handleSubmit,
  });
}
